import { create } from 'zustand'

import { AuthCookieService } from '@utils/AuthCookieService'
import { syncSentryUser } from '@libs/sentry/sentry'

export const ACCESS_TOKEN_URL_PARAM = 'accessToken'

export type AuthStatus = 'loading' | 'success' | 'failed' | undefined

export interface AuthState {
  accessToken?: string
  refreshToken?: string
  pendingAccessToken?: string
  isInitialized: boolean
  status: {
    signIn?: AuthStatus
    signUp?: AuthStatus
  }
}

export type SignInSuccessType = {
  accessToken: string
  refreshToken: string
}

export type SignUpSuccessType = {
  accessToken: string
  refreshToken: string
}

interface AuthStore {
  state: AuthState
  actions: {
    initializeAuthState: (payload: { accessToken?: string; pendingAccessToken?: string }) => void
    signUpLoading: () => void
    signUpError: () => void
    signUpCodeSent: () => void
    signUpSuccess: (payload: SignUpSuccessType) => void
    signInLoading: () => void
    signInError: () => void
    signInSuccess: (payload: SignInSuccessType) => void
    signOut: () => void
    switchAccount: (accessToken: string) => Promise<void>
    dismissPendingAccessToken: () => void
  }
}

const initialState: AuthState = {
  accessToken: undefined,
  refreshToken: undefined,
  pendingAccessToken: undefined,
  isInitialized: false,
  status: {
    signIn: undefined,
    signUp: undefined,
  },
}

export const useAuthStore = create<AuthStore>((set) => ({
  state: initialState,
  actions: {
    initializeAuthState: (payload) =>
      set(({ state }) => ({
        state: {
          ...state,
          accessToken: payload.accessToken,
          pendingAccessToken: payload.pendingAccessToken,
          isInitialized: true,
          status: {
            ...state.status,
            signIn: payload.accessToken ? 'success' : undefined,
          },
        },
      })),
    signUpLoading: () =>
      set(({ state }) => ({
        state: { ...state, status: { ...state.status, signUp: 'loading' } },
      })),
    signUpError: () =>
      set(({ state }) => ({
        state: { ...state, status: { ...state.status, signUp: 'failed' } },
      })),
    signUpCodeSent: () =>
      set(({ state }) => ({
        state: { ...state, status: { ...state.status, signUp: undefined } },
      })),
    signUpSuccess: (payload) => {
      set(({ state }) => ({
        state: {
          ...state,
          accessToken: payload.accessToken,
          refreshToken: payload.refreshToken,
          status: { ...state.status, signUp: 'success', signIn: 'success' },
        },
      }))
      AuthCookieService.storeAuthInfo({ accessToken: payload.accessToken, refreshToken: payload.refreshToken }, true)
    },
    signInLoading: () =>
      set(({ state }) => ({
        state: { ...state, status: { ...state.status, signIn: 'loading' } },
      })),
    signInError: () =>
      set(({ state }) => ({
        state: { ...state, status: { ...state.status, signIn: 'failed' } },
      })),
    signInSuccess: (payload) => {
      set(({ state }) => ({
        state: {
          ...state,
          accessToken: payload.accessToken,
          refreshToken: payload.refreshToken,
          status: { ...state.status, signIn: 'success' },
        },
      }))
      AuthCookieService.storeAuthInfo({ accessToken: payload.accessToken, refreshToken: payload.refreshToken }, true)
    },
    signOut: () => {
      set(({ state }) => ({
        state: {
          ...state,
          accessToken: undefined,
          refreshToken: undefined,
          status: { ...state.status, signIn: undefined, signUp: undefined },
        },
      }))
      AuthCookieService.clearAuthInfo()
      syncSentryUser(undefined)
    },
    switchAccount: (accessToken) => {
      set(({ state }) => ({
        state: {
          ...state,
          accessToken,
          refreshToken: undefined,
          pendingAccessToken: undefined,
          status: { ...state.status, signIn: 'success' },
        },
      }))
      return AuthCookieService.storeAuthInfo({ accessToken }, true)
    },
    dismissPendingAccessToken: () =>
      set(({ state }) => ({
        state: { ...state, pendingAccessToken: undefined },
      })),
  },
}))

export const initializeAuth = async (): Promise<void> => {
  const { initializeAuthState } = useAuthStore.getState().actions
  try {
    const queryParams = new URLSearchParams(window.location.search)
    const accessToken = queryParams.get(ACCESS_TOKEN_URL_PARAM)
    const authInfo = await AuthCookieService.getAuthInfo()

    if (accessToken && !authInfo) {
      await AuthCookieService.storeAuthInfo({ accessToken }, true)
      initializeAuthState({ accessToken })
    } else {
      const pendingAccessToken = accessToken && accessToken !== authInfo?.accessToken ? accessToken : undefined
      initializeAuthState({ accessToken: authInfo?.accessToken, pendingAccessToken })
    }
  } catch (err) {
    console.error('Error initializing auth state:', err)
    initializeAuthState({ accessToken: undefined })
  }
}
